import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from '../../utils/toast';
import {
  DollarSign,
  Calculator,
  Save,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  FileText
} from 'lucide-react';
import OffboardingStepSkeleton from './OffboardingStepSkeleton';
import HorizontalStepper from '../../components/UI/HorizontalStepper';
import { StatusBadge } from '../../components/UI/StatusBadge';
import WithdrawalModal from '../../components/WithdrawalModal';
import useWithdrawal from '../../hooks/useWithdrawal';
import useOffboardingWorkflow from '../../hooks/useOffboardingWorkflow';
import { getOffboardingSteps, calculateProgressPercentage } from '../../config/offboardingSteps';
import { initializeStepData } from '../../utils/workflowUtils';


const OffboardingStep5 = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { handleWithdrawal } = useWithdrawal();
  const { workflow, loading, saving: hookSaving, saveStep } = useOffboardingWorkflow(workflowId);
  const [saving, setSaving] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [stepData, setStepData] = useState({
    final_salary_amount: '',
    total_leave_encashment: '',
    total_bond_deduction: '',
    net_final_payment: '',
    settlement_calculation_details: '',
    settlement_approved: false,
    settlement_approved_by: '',
    settlement_approved_date: null
  });

  // Calculation state
  const [calculations, setCalculations] = useState({
    basicSalary: 0,
    leaveEncashment: 0,
    bondDeductions: 0,
    totalDeductions: 0,
    netPayment: 0
  });

  // Handle step click navigation
  const handleStepClick = (step) => {
    navigate(step.route);
  };

  // Initialize step data when workflow loads
  useEffect(() => {
    if (workflow) {
      const initialData = initializeStepData(workflow, 5);
      setStepData(initialData);
      // Initialize calculations
      calculateSettlement(workflow);
    }
  }, [workflow]);

  const calculateSettlement = (data) => {
    const basicSalary = parseFloat(data.final_salary_amount) || 0;
    const leaveEncashment = parseFloat(data.total_leave_encashment) || 0;
    const bondDeductions = parseFloat(data.total_bond_deduction) || 0;

    const totalAdditions = basicSalary + leaveEncashment;
    const totalDeductions = bondDeductions;
    const netPayment = totalAdditions - totalDeductions;

    setCalculations({
      basicSalary,
      leaveEncashment,
      bondDeductions,
      totalDeductions,
      netPayment: Math.max(0, netPayment) // Ensure non-negative
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Auto-calculate net payment if not manually set
      const netPayment = stepData.net_final_payment ||
        (calculations.basicSalary + calculations.leaveEncashment - calculations.bondDeductions);

      const saveData = {
        ...stepData,
        net_final_payment: netPayment.toString(),
        settlement_calculation_details: JSON.stringify({
          basic_salary: calculations.basicSalary,
          leave_encashment: calculations.leaveEncashment,
          bond_deductions: calculations.bondDeductions,
          total_additions: calculations.basicSalary + calculations.leaveEncashment,
          total_deductions: calculations.bondDeductions,
          net_payment: netPayment,
          calculated_at: new Date().toISOString()
        })
      };

      await saveStep(5, saveData);
    } catch (error) {
      console.error('Error saving step:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCalculate = () => {
    const basicSalary = parseFloat(stepData.final_salary_amount) || 0;
    const leaveEncashment = parseFloat(stepData.total_leave_encashment) || 0;
    const bondDeductions = parseFloat(stepData.total_bond_deduction) || 0;

    const netPayment = basicSalary + leaveEncashment - bondDeductions;

    setStepData(prev => ({
      ...prev,
      net_final_payment: netPayment.toString()
    }));

    setCalculations({
      basicSalary,
      leaveEncashment,
      bondDeductions,
      totalDeductions: bondDeductions,
      netPayment: Math.max(0, netPayment)
    });

    toast.success('Settlement calculated successfully');
  };

  const handleNext = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-6`);
  };

  const handlePrev = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-4`);
  };


  if (loading) {
    return <OffboardingStepSkeleton />;
  }

  if (!workflow) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Workflow Not Found</h2>
          <p className="text-gray-600">The requested offboarding workflow could not be found.</p>
        </div>
      </div>
    );
  }

  const steps = getOffboardingSteps(workflow, workflowId);
  const progressPercentage = calculateProgressPercentage(workflow);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white">
        <div className="px-6 py-8 space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                Step 5: Final Settlement
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                {workflow.employee.first_name} {workflow.employee.last_name} • {workflow.employee.staff_id}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">Step 5 of {workflow?.requires_ir21 ? 7 : 6}</span>
              {workflow.step_5_completed && (
                <StatusBadge status="completed" showIcon />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Horizontal Stepper */}
      <HorizontalStepper
        steps={steps}
        currentStep={5}
        onStepClick={handleStepClick}
        progressPercentage={progressPercentage}
      />

      {/* Main Content */}
      <div className="px-6 py-8 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calculation Inputs */}
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          <div className="flex items-center mb-4 sm:mb-6 min-w-0">
            <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Settlement Calculation</h2>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Final Salary Amount ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={stepData.final_salary_amount}
                onChange={(e) => setStepData({...stepData, final_salary_amount: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                placeholder="0.00"
                disabled={workflow.step_5_completed}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Leave Encashment Amount ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={stepData.total_leave_encashment}
                onChange={(e) => setStepData({...stepData, total_leave_encashment: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                placeholder="0.00"
                disabled={workflow.step_5_completed}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bond Deductions ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={stepData.total_bond_deduction}
                onChange={(e) => setStepData({...stepData, total_bond_deduction: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                placeholder="0.00"
                disabled={workflow.step_5_completed}
              />
            </div>

            {!workflow.step_5_completed && workflow.workflow_status !== 'withdrawn' && (
              <button
                onClick={handleCalculate}
                className="w-full btn btn-admin-secondary"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculate Settlement
              </button>
            )}
          </div>
        </div>

        {/* Calculation Summary */}
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          <div className="flex items-center mb-4 sm:mb-6 min-w-0">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Settlement Summary</h2>
          </div>

          <div className="space-y-4">
            {/* Additions */}
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-green-800 mb-3">Additions</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Basic Salary:</span>
                  <span className="font-medium">${calculations.basicSalary.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Leave Encashment:</span>
                  <span className="font-medium">${calculations.leaveEncashment.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-medium text-green-800 border-t border-green-200 pt-2">
                  <span>Total Additions:</span>
                  <span>${(calculations.basicSalary + calculations.leaveEncashment).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div className="bg-red-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-red-800 mb-3">Deductions</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Bond Deductions:</span>
                  <span className="font-medium">${calculations.bondDeductions.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-medium text-red-800 border-t border-red-200 pt-2">
                  <span>Total Deductions:</span>
                  <span>${calculations.totalDeductions.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Net Payment */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-blue-800">Net Final Payment:</span>
                <span className="text-2xl font-bold text-blue-600">
                  ${calculations.netPayment.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Manual Override */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Manual Net Payment Override ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={stepData.net_final_payment}
                onChange={(e) => setStepData({...stepData, net_final_payment: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                placeholder="Leave empty to use calculated amount"
                disabled={workflow.step_5_completed}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to use automatically calculated amount
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Approval Section */}
      <div className="bg-white rounded-2xl shadow-sm border p-8">
        <div className="flex items-center mb-4 sm:mb-6 min-w-0">
          <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Settlement Approval</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={stepData.settlement_approved}
                onChange={(e) => setStepData({...stepData, settlement_approved: e.target.checked})}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_5_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Settlement calculations approved</span>
            </label>

            {stepData.settlement_approved && (
              <div className="ml-6 space-y-2">
                <input
                  type="text"
                  value={stepData.settlement_approved_by}
                  onChange={(e) => setStepData({...stepData, settlement_approved_by: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  placeholder="Approved by (name)"
                  disabled={workflow.step_5_completed}
                />
                <input
                  type="datetime-local"
                  value={stepData.settlement_approved_date}
                  onChange={(e) => setStepData({...stepData, settlement_approved_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  disabled={workflow.step_5_completed}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <textarea
              value={stepData.settlement_calculation_details}
              onChange={(e) => setStepData({...stepData, settlement_calculation_details: e.target.value})}
              rows={4}
              className="textarea-admin"
              placeholder="Additional settlement notes or special conditions..."
              disabled={workflow.step_5_completed}
            />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={handlePrev}
          className="btn btn-admin-outline"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous Step
        </button>

        <div className="flex space-x-3">
          {!workflow.step_5_completed && workflow.workflow_status !== 'withdrawn' && (
            <button
              onClick={() => setShowWithdrawalModal(true)}
              className="btn btn-danger"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Withdraw
            </button>
          )}

          {!workflow.step_5_completed && workflow.workflow_status !== 'withdrawn' ? (
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-admin-secondary"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Progress'}
            </button>
          ) : null}

          <button
            onClick={handleNext}
            className="btn btn-admin-primary"
          >
            Next Step
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>

        {/* Withdrawal Modal */}
        <WithdrawalModal
          isOpen={showWithdrawalModal}
          onClose={() => setShowWithdrawalModal(false)}
          currentStep={5}
          onConfirmWithdraw={handleWithdrawal}
          workflow={workflow}
        />
        </div>
      </div>
    </div>
  );
};

export default OffboardingStep5;
